import api from "@/http/api";
import { useAuthStore } from "@/store";
import { useMutation, useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import DiscussionSection from "@univdiam/ui/customComponents/OrderDetail/DiscussionSection";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import TicketIcon from '@/assets/FeaturedIcon.svg'
import AddProduct3 from '@univdiam/ui/customComponents/forms/AddProduct3'
import { useGroup } from "@/sharedData/group";
import { useSocketStore } from "@/socketStore";
import Drawer from "@univdiam/ui/customComponents/Drawer";
type SendDataType = {
  message: string;
  media_url?: string;
  content_type: string
  user_id?: string
  order_id?: string
  metdata?: string
}

const getDiscussion = (orderId: string, page: number) => {
  return api.get(`/admin/discussion/${orderId}?page=${page}&limit=20`);
}
const postDiscussion = (values: SendDataType) => {
  return api.post('/admin/discussion', values)
}
const patchDiscussionMessage = (id: string, message: string) => {
  return api.patch(`/admin/discussion/${id}/message`, { message })
}
const deleteDiscussionMessage = (id: string) => {
  return api.delete(`/admin/discussion/${id}`)
}


type DiscussionProps = {
  orderRefProp?: string;
  orderIdProp?: string;
  customerIdProp?: string;
  headerActions?: React.ReactNode;
};

const Discussion = ({ orderRefProp, orderIdProp, customerIdProp, headerActions }: DiscussionProps = {}) => {
  const { user } = useAuthStore()
  const params = useParams()
  const orderRef = orderRefProp || params.orderRef
  const orderId = orderIdProp || params.id
  const customer_id = customerIdProp || params.customer_id

  const [createProduct, setCreateProduct] = useState(false)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending } = useInfiniteQuery({
    queryKey: ["discussion", orderId],
    queryFn: async ({ pageParam = 1 }) => {
      return await getDiscussion(orderId!, pageParam as number);
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: any) => {
      const { page, totalPages } = lastPage.data.pagination;
      return page < totalPages ? page + 1 : undefined;
    },
    enabled: !!orderId
  });

  const discussionMessages = useMemo(() => {
    return data?.pages ? [...data.pages].reverse().flatMap((page: any) => page.data.discussion) : [];
  }, [data?.pages]);



  const { mutate, isPending: isDicussionPending } = useMutation({
    mutationFn: (values: SendDataType) => {
      return postDiscussion({
        ...values,
        order_id: orderId,
        user_id: user?.id,
      })
    },
    onSuccess: async () => {
      toast.success('Discussion added successfully', {
        position: 'top-center'
      })
      setCreateProduct(false)

      sendNotification({
        action_by: user?.role!,
        action_by_id: user?.id.toString()!,
        action_type: 'discussion_message',
        order_id: orderId!,
        title: 'New Discussion Message',
        metadata: {
          order_id: orderId,
        }
      });

      queryClient.invalidateQueries({ queryKey: ['discussion', orderId], })
      queryClient.invalidateQueries({ queryKey: ['all-admin-discussions'] })
      queryClient.invalidateQueries({ queryKey: ['sidebar-discussion-unread'] })
    },
    onError: async (err: any) => {
      const errorMsg = err?.response?.data?.error || err?.message || 'Something went wrong!';
      toast.error(errorMsg)
    }
  })


  const handleSendDiscussion = (values: SendDataType) => {
    mutate(values)
  }

  // Both refetch the thread plus the two sidebar queries, because an edit
  // changes the AllDiscussion preview text and a delete changes the preview,
  // the unread count, or both.
  const invalidateDiscussion = () => {
    queryClient.invalidateQueries({ queryKey: ['discussion', orderId] })
    queryClient.invalidateQueries({ queryKey: ['all-admin-discussions'] })
    queryClient.invalidateQueries({ queryKey: ['sidebar-discussion-unread'] })
  }

  // Awaited by DiscussionSection: it keeps the inline editor open and the draft
  // intact when this rejects, so a 403 from the lapsed edit window does not
  // discard what the user typed.
  const handleEditDiscussion = async (id: string, message: string) => {
    try {
      await patchDiscussionMessage(id, message)
      invalidateDiscussion()
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not edit this message.')
      throw err
    }
  }

  const handleDeleteDiscussion = async (id: string) => {
    try {
      await deleteDiscussionMessage(id)
      toast.success('Message deleted', { position: 'top-center' })
      invalidateDiscussion()
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not delete this message.')
      throw err
    }
  }


  const [isMediaUploading, setIsMediaUploading] = useState(false)
  const handleSendToUpload = async (files: File[]) => {
    setIsMediaUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await api.post(
        `/upload?customerId=${user?.id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      const data = response.data;

      return data.files ?? [];

    } catch (error) {
      toast.error(`Error occured while uploading media : ${error}`)
    } finally {
      setIsMediaUploading(false);
    }
  };



  const handleAddProductOpen = () => {
    setCreateProduct(true)
  }

  const { data:groupResponse } = useGroup()
  const groupData = groupResponse?.data?.productGroups
  const sendNotification = useSocketStore((s) => s.sendNotification);

  // Mark discussions as read when viewing this order
  useEffect(() => {
    if (discussionMessages && discussionMessages.length > 0 && orderId) {
      api.patch(`/admin/discussion/read-all/${orderId}`).then((res) => {
        if (res.data?.success || res.data?.status) {
          queryClient.invalidateQueries({ queryKey: ["sidebar-discussion-unread"] });
          queryClient.invalidateQueries({ queryKey: ["all-admin-discussions"] });
          if (res.data?.unseenCount !== undefined) {
            sendNotification({
              action_by: user?.role!,
              action_by_id: user?.id.toString()!,
              action_type: 'discussion_read',
              order_id: orderId,
              title: `Discussion Read Receipt`,
              metadata: {
                order_ref: orderRef,
              },
            });
          }
        }
      }).catch(console.error);
    }
  }, [discussionMessages, orderId, orderRef, queryClient, sendNotification, user]);

  const handleShowProduct = (id: string) => {
    const product = discussionMessages.find((d:{id:string}) => d.id === id)
    navigate(`/product-view/${id}?from=discussion&order_id=${orderRef}&order_ref=${orderId}&customer_id=${customer_id}`, {
      state: { ...product },
    })
  }

  return (
    <>
      {
        createProduct && <Drawer
          open={createProduct}
          setOpen={setCreateProduct}
          contentClassName="lg:max-w-[900px]"
          title={
            <p className='flex items-center gap-[8px]'>
              <img src={TicketIcon} alt="Upload a product" />
              Upload a product
            </p>
          }
        >
          <div className='w-full pb-4'>
            <AddProduct3
              handleSendToUpload={handleSendToUpload}
              mediaLoading={isMediaUploading}
              handleSendDiscussion={handleSendDiscussion}
              isDicussionPending={isDicussionPending}
              isDiscussionLoading={isPending}
              groupData={groupData || []}
            />
          </div>
        </Drawer>
      }

      <DiscussionSection
        isDiscussionLoading={isPending}
        isDicussionPending={isDicussionPending}
        handleSendDiscussion={handleSendDiscussion}
        isAdmin={true}
        Discussion={discussionMessages}
        handleSendToUpload={handleSendToUpload}
        mediaLaoding={isMediaUploading}
        handleAddProductOpen={() => setCreateProduct(true)}
        handleShowProduct={handleShowProduct}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        headerActions={headerActions}
        currentUserId={user?.id?.toString()}
        handleEditDiscussion={handleEditDiscussion}
        handleDeleteDiscussion={handleDeleteDiscussion}
      />
    </>

  )
}

export default Discussion